import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { useAuth } from "@/auth/AuthContext";
import type { CommunityPost } from "@/api/lms/community";

type CommunitySyncResponse = { courseIds: string[] };
type CommunityJoinResponse = { communityId: string; courseId: string };

type CommunitySendPostPayload = {
  courseId: string;
  content: string;
  parentId?: string | null;
  attachmentUrl?: string | null;
  attachmentType?: "IMAGE" | "VIDEO" | "PDF" | "AUDIO" | null;
};

function apiUrlFromEnv(): string {
  return import.meta.env.VITE_API_URL ?? "http://127.0.0.1:3025";
}

export function useCommunitySocket() {
  const { tokens } = useAuth();

  const apiUrl = useMemo(() => apiUrlFromEnv(), []);

  const socketRef = useRef<Socket | null>(null);
  const [socketState, setSocketState] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // If the user is logged out (or access token not ready), ensure we have no active socket.
    if (!tokens?.accessToken) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocketState(null);
      setIsConnected(false);
      return;
    }

    const socket = io(apiUrl, {
      // Match server: start with HTTP long-polling so prod works behind proxies
      // that break wss upgrades; Socket.IO upgrades to websocket when possible.
      transports: ["polling", "websocket"],
      auth: { token: tokens.accessToken },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socket;
    setSocketState(socket);

    const onConnect = () => {
      setIsConnected(true);
      // Join all course rooms the user belongs to.
      socket.emit("community:sync");
    };
    const onDisconnect = () => setIsConnected(false);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    // Avoid hanging on old listeners when access token changes.
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.disconnect();
      socketRef.current = null;
      setSocketState(null);
      setIsConnected(false);
    };
  }, [apiUrl, tokens?.accessToken]);

  const joinCourse = useCallback(async (courseId: string): Promise<CommunityJoinResponse> => {
    const socket = socketRef.current;
    if (!socket) throw new Error("Socket not connected");

    // Avoid ACK-timeouts: if the server throws (e.g. not a member),
    // socket.io ACK might never resolve. We rely on REST for initial data,
    // and realtime events for updates.
    socket.emit("community:join", { courseId });
    return { communityId: "", courseId };
  }, []);

  const sendPost = useCallback(
    async (payload: CommunitySendPostPayload): Promise<CommunityPost> => {
      const socket = socketRef.current;
      if (!socket) throw new Error("Socket not connected");

      // Wait for ACK from the server (Nest returns the saved post as the ack payload).
      // Use a short timeout so errors don't look like "nothing happened".
      return await new Promise<CommunityPost>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          reject(new Error("Send timed out. Please try again."));
        }, 7000);

        socket.emit(
          "community:send-post",
          payload,
          (resp: CommunityPost) => {
            window.clearTimeout(timeoutId);
            resolve(resp);
          },
        );
      });
    },
    [],
  );

  return {
    socket: socketState,
    isConnected,
    joinCourse,
    sendPost,
  };
}

